import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { PAYLOAD_QUERY } from '@/components/customercenter-ui/components/customer-detail/constants';
import CreditCardsIcon from '@/components/customercenter-ui/icons/creditCards';
import { Label } from '@/components/customercenter-ui/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/customercenter-ui/ui/radio-group';
import { getDivisionSvgs } from '@/components/customercenter-ui/utils/crediCardUtils';
import { PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';

export function CardRadioButton({ data, isDisabled }: any) {
  const { selectedPaymentMethod, setSelectedPaymentMethods, isNoChargeOrder, isMailOrder } = useCheckout();
  const projectSettings: ProjectSettings = useProjSettings();
  const params = useSearchParams();
  const allowedPaymentMethods = JSON.parse(projectSettings?.allowedPaymentMethods ?? '');
  const { showCashOption = false, showCheckOption = false } = data ?? {};
  const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? 'ZB';
  const allowedMethodsForDivision: any = allowedPaymentMethods?.[divisionCode];

  const handlePaymentMethodChange = (event: PaymentMethods) => {
    setSelectedPaymentMethods(event);
  };

  const renderComponent = () => {
    switch (divisionCode) {
      case 'ZD':
        return 'Mason Easy-Pay®';
      case 'ZB':
        return 'Stoneberry Credit';
      case 'ZY':
        return 'Masseys Credit';
      default:
        return 'Stoneberry Credit';
    }
  };

  const renderPaymentMethods = () => {
    if (isNoChargeOrder) {
      return <></>;
    }
    return (
      <>
        {' '}
        {allowedMethodsForDivision.includes(PaymentMethods.CARD) && (
          <section
            className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handlePaymentMethodChange(PaymentMethods.CARD);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handlePaymentMethodChange(PaymentMethods.CARD);
              }
            }}
          >
            <RadioGroupItem value={PaymentMethods.CARD} id="r1" />
            <Label htmlFor="r1" className="flex w-full items-center justify-between pl-2">
              <p className="py-4 text-sm font-normal leading-6 md:text-lg">Credit/Debit Card</p>
              <CreditCardsIcon className="gbomit h-9 w-[149px] pr-4" />
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CREDIT) && (
          <section
            className={`flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md ${
              isDisabled ? 'disabled-section' : ''
            }`}
            tabIndex={isDisabled ? -1 : 0}
            onClick={(e) => {
              if (isDisabled) return;
              e.stopPropagation();
              handlePaymentMethodChange(PaymentMethods.CREDIT);
            }}
            onKeyDown={(e) => {
              if (isDisabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handlePaymentMethodChange(PaymentMethods.CREDIT);
              }
            }}
          >
            <RadioGroupItem value={PaymentMethods.CREDIT} id="r2" disabled={isDisabled} />
            <Label htmlFor="r2" className="flex w-full items-center justify-between pl-2">
              <p className="py-4 text-sm font-normal leading-6 md:text-lg">{renderComponent()}</p>
              <div className="gbomit mr-4 flex w-24 items-start">
                {getDivisionSvgs(divisionCode, 'gbomit h-9 w-[149px] pr-4')}
              </div>
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CASH) && showCashOption && isMailOrder && (
          <section
            className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handlePaymentMethodChange(PaymentMethods.CASH);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handlePaymentMethodChange(PaymentMethods.CASH);
              }
            }}
          >
            <RadioGroupItem value={PaymentMethods.CASH} id="r3" />
            <Label htmlFor="r3" className="flex w-full items-center justify-between pl-2">
              <p className="py-4 text-lg font-normal leading-6">Cash</p>
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CHECK) && showCheckOption && isMailOrder && (
          <section
            className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handlePaymentMethodChange(PaymentMethods.CHECK);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handlePaymentMethodChange(PaymentMethods.CHECK);
              }
            }}
          >
            <RadioGroupItem value={PaymentMethods.CHECK} id="r4" />
            <Label htmlFor="r4" className="flex w-full items-center justify-between pl-2">
              <p className="py-4 text-lg font-normal leading-6">Check(s)</p>
            </Label>
          </section>
        )}
      </>
    );
  };

  return (
    <>
      {selectedPaymentMethod && (
        <RadioGroup
          defaultValue={selectedPaymentMethod}
          onValueChange={(value: PaymentMethods) => handlePaymentMethodChange(value)}
          className="grid-col grid grid-cols-2 gap-3"
        >
          {renderPaymentMethods()}
        </RadioGroup>
      )}
    </>
  );
}
